/**
 * Inline editor for a single Spec membership within a section.
 *
 * Fields are always editable. If the Spec is inherited (not owned at the
 * current scope), the `canAutoCustomize` flag is surfaced so the parent's
 * onSaveSpec handler can silently create a child override at this scope
 * before applying the edits. The editor itself doesn't decide when to
 * customize — it just passes along the patch.
 *
 * Beyond the interview-question fields (prompt/guidance/category/priority/
 * roles), this exposes the structured-spec fields (spec_type, unit,
 * category_options) so PMs can author typed specs, not just free text.
 */
import { useState } from 'react'
import { X, Save, Trash2, Plus } from 'lucide-react'
import Badge from '../ui/Badge'
import {
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  ROLE_OPTIONS,
  SPEC_TYPE_OPTIONS,
} from './types'
import type {
  CategoryOption,
  Spec,
  SpecCategory,
  SpecPriority,
  SpecType,
  SectionSpecMembership,
} from './types'

interface Props {
  membership: SectionSpecMembership
  /** True when the underlying Spec is inherited; saving will create a local
   * override at the current scope. Surfaced in UI as a hint. */
  canAutoCustomize?: boolean
  onSaveSpec: (s: Partial<Spec>) => Promise<void>
  onSaveMembership: (m: Partial<SectionSpecMembership>) => Promise<void>
  onRemove: () => Promise<void>
  onClose: () => void
}

const TYPES_WITH_OPTIONS: SpecType[] = ['categorical', 'ordered_categorical']

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80)
}

export default function SpecEditor({
  membership,
  canAutoCustomize,
  onSaveSpec,
  onSaveMembership,
  onRemove,
  onClose,
}: Props) {
  const s = membership.spec_detail
  const [prompt, setPrompt] = useState(s.prompt || '')
  const [guidance, setGuidance] = useState(s.guidance || '')
  const [category, setCategory] = useState<SpecCategory | ''>(s.category || '')
  const [priority, setPriority] = useState<SpecPriority | ''>(s.interview_priority || '')
  const [specType, setSpecType] = useState<SpecType>(s.spec_type || 'text')
  const [unit, setUnit] = useState(s.unit || '')
  const [options, setOptions] = useState<CategoryOption[]>(s.category_options || [])
  const [roles, setRoles] = useState<string[]>(s.eligible_respondent_roles || [])
  const [required, setRequired] = useState(membership.required)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleRole = (r: string) => {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))
  }

  const showOptions = TYPES_WITH_OPTIONS.includes(specType)
  const showUnit = specType === 'numeric'

  const setOptionLabel = (idx: number, label: string) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? { value: o.value || slugify(label), label } : o)))
  }
  const setOptionValue = (idx: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, value } : o)))
  }
  const addOption = () => setOptions((prev) => [...prev, { value: '', label: '' }])
  const removeOption = (idx: number) => setOptions((prev) => prev.filter((_, i) => i !== idx))

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const cleanedOptions = options
        .map((o) => ({ value: (o.value || slugify(o.label)).trim(), label: o.label.trim() }))
        .filter((o) => o.value && o.label)
      await onSaveSpec({
        prompt: prompt || null,
        guidance: guidance || null,
        category: (category || null) as SpecCategory | null,
        interview_priority: (priority || null) as SpecPriority | null,
        spec_type: specType,
        unit: showUnit ? unit : '',
        category_options: showOptions ? cleanedOptions : [],
        eligible_respondent_roles: roles.length ? roles : null,
      })
      if (required !== membership.required) {
        await onSaveMembership({ required })
      }
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-muted-foreground">{s.key}</span>
          <Badge variant="info">{s.owner_level}</Badge>
          {canAutoCustomize && <Badge variant="pending">Inherited</Badge>}
          {s.is_removed && <Badge variant="rejected">Removed</Badge>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-card-foreground">Prompt</span>
          <textarea
            className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
            rows={2}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Leave empty to inherit from parent"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-card-foreground">Guidance</span>
          <textarea
            className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
            rows={2}
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder="Hints for the agent or respondent (optional)"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-card-foreground">Answer type</span>
            <select
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={specType}
              onChange={(e) => setSpecType(e.target.value as SpecType)}
            >
              {SPEC_TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>

          {showUnit && (
            <label className="block">
              <span className="text-sm font-medium text-card-foreground">Unit</span>
              <input
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="e.g. USD, days"
              />
            </label>
          )}
        </div>

        {showOptions && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-card-foreground">Options</span>
            {options.length === 0 && (
              <div className="text-xs text-muted-foreground italic">No options yet.</div>
            )}
            {options.map((o, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-foreground text-sm"
                  value={o.label}
                  onChange={(e) => setOptionLabel(idx, e.target.value)}
                  placeholder="Label (e.g. High)"
                />
                <input
                  className="w-40 px-3 py-1.5 rounded-md border border-input bg-background text-foreground font-mono text-xs"
                  value={o.value}
                  onChange={(e) => setOptionValue(idx, e.target.value)}
                  placeholder="value"
                />
                <button
                  type="button"
                  onClick={() => removeOption(idx)}
                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
                  aria-label="Remove option"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addOption}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-dashed border-border rounded-md text-muted-foreground hover:text-foreground hover:border-primary"
            >
              <Plus className="w-3 h-3" />
              Add option
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-card-foreground">Category</span>
            <select
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={category}
              onChange={(e) => setCategory(e.target.value as SpecCategory | '')}
            >
              <option value="">(inherit)</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-card-foreground">Priority</span>
            <select
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={priority}
              onChange={(e) => setPriority(e.target.value as SpecPriority | '')}
            >
              <option value="">(inherit)</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <span className="text-sm font-medium text-card-foreground">Eligible respondent roles</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {ROLE_OPTIONS.map((r) => {
              const selected = roles.includes(r.value)
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => toggleRole(r.value)}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    selected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-muted-foreground border-border hover:border-primary'
                  }`}
                >
                  {r.label}
                </button>
              )
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          <span>Required for completion (this section)</span>
        </label>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="flex justify-between items-center pt-2 border-t border-border gap-3">
        <div className="flex-1 min-w-0">
          {canAutoCustomize && (
            <div className="text-xs text-muted-foreground italic">
              Saving will create a customization at this scope.
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!confirm('Remove this spec from the section?')) return
              await onRemove()
              onClose()
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 rounded-md"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove from section
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
